import { memo } from 'react';

import { Link, useParams } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Button, ScrollArea, Skeleton } from '@/_components';
import type { RouteListItem } from '@/types';

import { getButtonIconStyle, getButtonTextStyle } from './helper';

function SecondaryNavbarBase({
  subNavLinks,
  inDrawer,
  callback,
  isLoading,
}: {
  subNavLinks: RouteListItem[];
  inDrawer?: boolean;
  callback?: () => void;
  isLoading?: boolean;
}) {
  const { id } = useParams({ strict: false });

  const shouldUseScroll = subNavLinks.length > 8;

  const items = (
    <div className={`flex flex-col gap-y-1.5 px-2 ${inDrawer ? 'pb-10' : 'py-5'}`}>
      {subNavLinks.map((item, i) => {
        if (isLoading) {
          return <Skeleton key={i} className="h-15" />;
        }

        if (!item.show) {
          return null;
        }

        return (
          <Link
            activeOptions={{ exact: item.isDoubleNested ? false : true }}
            key={item.path + '' + i}
            to={item.path}
            params={{ id: `${id}` }}
            replace
            onClick={() => {
              if (typeof callback === 'function') {
                callback();
              }
            }}
          >
            {({ isActive }: { isActive: boolean }) => (
              <Button
                size="sm"
                icon={item.icon}
                iconClassName={twMerge(item.isDisabled ? 'm-0' : getButtonIconStyle(isActive))}
                className={twMerge(
                  getButtonTextStyle(isActive),
                  'disabled:bg-transparent dark:disabled:bg-transparent',
                  'disabled:border-none dark:disabled:border-none',
                  'disabled:text-gray-600 dark:disabled:text-gray-600',
                  'disabled:cursor-default disabled:opacity-40'
                )}
                disabled={item.isDisabled}
              >
                {item.name}
              </Button>
            )}
          </Link>
        );
      })}
    </div>
  );

  return (
    <div
      className={`${
        inDrawer && 'h-full pt-8'
      } border-c-border dark:bg-gray-850 flex h-full w-28 shrink-0 flex-col border-r bg-gray-50`}
      style={{ contain: 'layout paint' }}
    >
      {shouldUseScroll ? (
        <ScrollArea
          className={`${inDrawer ? 'h-full' : 'h-[calc(100vh-64px)]'} w-full transform-gpu overflow-x-hidden`}
        >
          {items}
        </ScrollArea>
      ) : (
        <div className={`${inDrawer ? 'h-full' : 'h-[calc(100vh-64px)]'} w-full overflow-x-hidden`}>{items}</div>
      )}
    </div>
  );
}

export const SecondaryNavbar = memo(SecondaryNavbarBase);
